

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MacroCurriculumForm } from './MacroCurriculumForm';
import { MesoCurriculumForm } from './MesoCurriculumForm';
import {
  MacroCurriculum,
  MesoCurriculum,
  createMacroCurriculum,
  updateMacroCurriculum,
  createMesoCurriculum,
  updateMesoCurriculum
} from '@/services/roadmap/roadmapService';
import { Belt } from '@/components/roadmap/timeline/types';
import { MacroCurriculumFormValues, MesoCurriculumFormValues } from './schema';

type CurriculumDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: 'macro' | 'meso' | 'insights';
  mode: 'create' | 'edit';
  initialData?: MacroCurriculum | MesoCurriculum;
  onSuccess?: () => void;
  belt?: Belt | null;
  curriculum?: MacroCurriculum | MesoCurriculum | null;
  onClose?: () => void;
};

export function CurriculumDialog({
  open,
  onOpenChange,
  type,
  mode,
  initialData,
  onSuccess,
  belt,
  curriculum,
  onClose,
}: CurriculumDialogProps) {
  // If this is a view dialog (belt & curriculum are provided)
  if (belt && curriculum) {
    return (
      <Dialog open={!!belt} onOpenChange={() => onClose?.()}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{curriculum.title}</DialogTitle>
            <DialogDescription>
              {belt.stripes
                ? `${belt.name} belt with ${belt.stripes} stripe${belt.stripes > 1 ? 's' : ''}`
                : `${belt.name} belt`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4 space-y-6">
            {curriculum.content && (
              <div className="prose prose-stone dark:prose-invert max-w-none">
                <div dangerouslySetInnerHTML={{ __html: curriculum.content }} />
              </div>
            )}
            
            {curriculum.image_url && (
              <div className="rounded-md overflow-hidden">
                <img 
                  src={curriculum.image_url} 
                  alt={curriculum.title} 
                  className="w-full h-auto object-cover"
                />
              </div>
            )}
            
            {curriculum.video_url && (
              <div>
                <h3 className="text-lg font-medium mb-2">Video Demonstration</h3>
                <div className="aspect-video">
                  <iframe 
                    src={curriculum.video_url} 
                    className="w-full h-full rounded-md"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            )}
            
            {'key_techniques' in curriculum && curriculum.key_techniques && curriculum.key_techniques.length > 0 && (
              <div>
                <h3 className="text-lg font-medium mb-2">Key Techniques</h3>
                <ul className="list-disc pl-5 space-y-1">
                  {curriculum.key_techniques.map((technique, index) => (
                    <li key={index}>{technique}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {'learning_objectives' in curriculum && curriculum.learning_objectives && curriculum.learning_objectives.length > 0 && (
              <div>
                <h3 className="text-lg font-medium mb-2">Learning Objectives</h3>
                <ul className="list-disc pl-5 space-y-1">
                  {curriculum.learning_objectives.map((objective, index) => (
                    <li key={index}>{objective}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  // This is an admin dialog for creating/editing curriculum
  const title =
    mode === 'create'
      ? `Create ${type === 'macro' ? 'Macro' : 'Meso'} Curriculum Entry`
      : `Edit ${type === 'macro' ? 'Macro' : 'Meso'} Curriculum Entry`;

  const description =
    type === 'macro'
      ? 'Macro curriculum defines the high-level roadmap for each belt rank.'
      : 'Meso curriculum defines specific techniques and objectives for each belt stripe level.';

  const handleSubmitMacro = async (data: MacroCurriculumFormValues) => {
    if (mode === 'edit' && initialData?.id) {
      await updateMacroCurriculum(initialData.id, data);
    } else {
      await createMacroCurriculum(data as MacroCurriculum);
    }
    onSuccess?.();
    onOpenChange(false);
  };

  const handleSubmitMeso = async (data: MesoCurriculumFormValues) => {
    if (mode === 'edit' && initialData?.id) {
      await updateMesoCurriculum(initialData.id, data);
    } else {
      await createMesoCurriculum(data as MesoCurriculum);
    }
    onSuccess?.();
    onOpenChange(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {type === 'macro' && (
          <MacroCurriculumForm
            mode={mode}
            initialData={initialData as MacroCurriculum}
            onSubmit={handleSubmitMacro}
            onCancel={handleCancel}
            onSuccess={onSuccess}
          />
        )}

        {type === 'meso' && (
          <MesoCurriculumForm
            mode={mode}
            initialData={initialData as MesoCurriculum}
            onSubmit={handleSubmitMeso}
            onCancel={handleCancel}
            onSuccess={onSuccess}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
